/** @prettier */

import {useCallback, useEffect, useState} from 'react';
import {getCompositionsSitesBySiteID} from '../../API/API_Collection';
import {sub, add, format} from 'date-fns';
import {utcToZonedTime, zonedTimeToUtc} from 'date-fns-tz';

/**
 * グラフの初回描画時にグラフの描画する日付を取得するフック
 * @param siteID サイトID
 * @returns グラフの描画する日付
 */
export const useGetStartEndDate = (siteID: string | undefined) => {
  const [timeZone, setTimeZone] = useState(''); //タイムゾーン
  const [graphStartDate, setGraphStartDate] = useState(''); //グラフ開始日
  const [graphEndDate, setGraphEndDate] = useState(''); //グラフ最終日
  const [comparisonGraphStartDate, setComparisonGraphStartDate] = useState(''); //比較グラフ開始日
  const [comparisonGraphEndDate, setComparisonGraphEndDate] = useState(''); //比較グラフ最終日

  /**
   * グラフの開始日と最終日をセットする関数
   * @param start グラフのデータ取得開始日
   * @param end グラフのデータ取得終了日
   */
  const setGraphDispDate = useCallback((start: string, end: string) => {
    setGraphStartDate(start);
    setGraphEndDate(end);
  }, []);

  /**
   * 比較グラフの開始日と最終日をセットする関数
   * @param start 比較グラフのデータ取得開始日
   * @param end 比較グラフのデータ取得終了日
   */
  const setComparisonGraphDispDate = useCallback(
    (start: string, end: string) => {
      setComparisonGraphStartDate(start);
      setComparisonGraphEndDate(end);
    },
    []
  );

  //初回グラフ描画用の日時を設定する
  useEffect(() => {
    if (siteID === undefined) return;

    getCompositionsSitesBySiteID(siteID).then(response => {
      //タイムゾーンの取得
      setTimeZone(response['timezone']);
      //ユーザが選択したタイムゾーンの現地時刻
      const now = utcToZonedTime(new Date(), response['timezone']);
      //開始日を現在日とする
      const start = format(now, 'yyyy-MM-dd 00:00:00');
      const startUTC = zonedTimeToUtc(
        start,
        response['timezone']
      ).toISOString();
      setGraphStartDate(startUTC);
      //終了日を現在日＋１とする
      const end = format(add(now, {days: 1}), 'yyyy-MM-dd 00:00:00');
      const endUTC = zonedTimeToUtc(end, response['timezone']).toISOString();
      setGraphEndDate(endUTC);
      //比較グラフの開始日を現在日-1とする
      const comparisonStart = format(
        sub(now, {days: 1}),
        'yyyy-MM-dd 00:00:00'
      );
      const comparisonStartUTC = zonedTimeToUtc(
        comparisonStart,
        response['timezone']
      ).toISOString();
      setComparisonGraphStartDate(comparisonStartUTC);
      //比較グラフの終了日を現在日とする
      setComparisonGraphEndDate(startUTC);
    });
  }, [siteID]);

  return {
    timeZone,
    graphStartDate,
    graphEndDate,
    comparisonGraphStartDate,
    comparisonGraphEndDate,
    setGraphDispDate,
    setComparisonGraphDispDate,
  };
};
